import React, { useEffect, useState, useRef } from "react";
import IconButton from "@mui/material/IconButton";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  CircularProgress,
  Button,
  TextField,
  Box,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "react-quill/dist/quill.snow.css";
import CustomTypography from "../../globalStyles/fonts";
import { countryService } from "../../utils/services/allCountry";
import { isMobile } from "../../globalStyles/mobile";

const ManageClient = ({
  formData,
  handleClose,
  open,
  onClickAdd,
  title,
  formErrors,
  selectedClientData,
  onChangeData,
}) => {
  const modalRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countryError, setCountryError] = useState("");

  useEffect(() => {
    if (!open) return;

    let isActive = true;
    const fetchCountries = async () => {
      setLoading(true);
      setCountryError("");
      try {
        const data = await countryService.fetchAll();
        if (isActive) setCountries(data);
      } catch (error) {
        console.error("Failed to fetch countries:", error);
        if (isActive) {
          setCountryError("Unable to load countries. Please try again.");
        }
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchCountries();
    return () => {
      isActive = false;
    };
  }, [open]);

  const handleCountryChange = (e) => {
    const selectedCountry = countries.find(
      (country) => country.name === e.target.value,
    );
    if (!selectedCountry) return;

    onChangeData("country", selectedCountry.name);
    onChangeData("contactCode", selectedCountry.contactCode);
  };

  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose && handleClose();
      }}
      aria-labelledby="form-dialog-title"
      maxWidth="md"
      fullWidth
      ref={modalRef}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          id="form-dialog-title"
          style={{
            fontFamily: "Poppins",
            fontWeight: 500,
            color: "var(--text-primary)",
          }}
        >
          <CustomTypography fontWeight={500} fontSize={isMobile() ? 16 : 24}>
            {`${formData?._id ? "Edit" : "Create"} Client`}
          </CustomTypography>
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 20 }} />

      <DialogContent
        ref={modalRef}
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          scrollBehavior: "smooth",
        }}
        sx={{
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "7px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "var(--bg-paper)",
            borderRadius: 14,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "var(--scrollbar-thumb, #273894)",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "var(--scrollbar-thumb-hover, #6C9FFF)",
            },
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>
              {`Client Name`}&nbsp;
              <span style={{ color: "red" }}>*</span>
            </CustomTypography>

            <TextField
              sx={{ marginTop: 1, fontSize: 13, fontStyle: "Poppins" }}
              size="small"
              placeholder={`Enter ${title} Name`}
              variant="outlined"
              value={formData.name}
              onChange={(e) => onChangeData("name", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>
              Email&nbsp;
              <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              disabled={selectedClientData?.email ? true : false}
              sx={{ marginTop: 1 }}
              type="email"
              size="small"
              placeholder="Enter Email"
              variant="outlined"
              value={formData.email}
              onChange={(e) => onChangeData("email", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>Country</CustomTypography>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <TextField
                select
                sx={{ marginTop: 1 }}
                size="small"
                variant="outlined"
                value={formData.country || ""}
                onChange={handleCountryChange}
                fullWidth
                error={!!formErrors.country || !!countryError}
                helperText={formErrors.country || countryError}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Select Country
                </MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country.name} value={country.name}>
                    {country.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} sm={2}>
            <CustomTypography fontSize={15}>Code</CustomTypography>
            <TextField
              inputMode="numeric"
              sx={{
                marginTop: 1,
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "var(--hover-bg)",
                  color: "var(--text-secondary)",
                },
              }}
              type="text"
              size="small"
              variant="outlined"
              value={formData.contactCode}
              onChange={(e) => onChangeData("contactCode", e.target.value)}
              fullWidth
              disabled={formData.contactCode ? true : false}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomTypography fontSize={15}>
              Mobile Number <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              inputMode="numeric"
              sx={{ marginTop: 1 }}
              type="mobileNumber"
              size="small"
              placeholder="Enter Mobile No."
              variant="outlined"
              value={formData.contactNumber}
              onChange={(e) => onChangeData("contactNumber", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.mobileNumber}
              helperText={formErrors.mobileNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>
              Business Name <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              sx={{ marginTop: 1 }}
              type="text"
              size="small"
              placeholder="Enter Business Name"
              variant="outlined"
              value={formData.businessName}
              onChange={(e) => onChangeData("businessName", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.businessName}
              helperText={formErrors.businessName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>
              Managed Orchestration
            </CustomTypography>
            <Box sx={{ display: "flex", alignItems: "center", marginTop: 1 }}>
              <Switch
                checked={formData.managed_orchestration || false}
                onChange={(e) =>
                  onChangeData("managed_orchestration", e.target.checked)
                }
                color="primary"
              />
              <CustomTypography fontSize={13} color="var(--text-secondary)">
                {formData.managed_orchestration ? "Enabled" : "Disabled"}
              </CustomTypography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: 15 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => handleClose()}
          // className="cancel-button-style"
          style={{ textTransform: "none" }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={(e) => onClickAdd(e)}
          className="add-button-style"
          style={{ textTransform: "none" }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageClient;
